const DEFAULT_BASE_URL = 'https://sdk.hancom.com';

// 문자열 메서드로 슬래시 제거 (ReDoS 방지)
const trimTrailingSlashes = (str: string): string => {
  let end = str.length;
  while (end > 0 && str[end - 1] === '/') {
    end--;
  }
  return str.slice(0, end);
};

const trimLeadingSlashes = (str: string): string => {
  let start = 0;
  while (start < str.length && str[start] === '/') {
    start++;
  }
  return str.slice(start);
};

const sanitizeBaseUrl = (url: string | undefined): string => {
  const trimmed = url?.trim();
  if (!trimmed) {
    return DEFAULT_BASE_URL;
  }

  if (!/^https?:\/\//i.test(trimmed)) {
    return trimTrailingSlashes(`https://${trimLeadingSlashes(trimmed)}`) || DEFAULT_BASE_URL;
  }

  return trimTrailingSlashes(trimmed);
};

const sanitizeOptionalBaseUrl = (url: string | undefined): string | undefined => {
  const trimmed = url?.trim();
  if (!trimmed) {
    return undefined;
  }

  if (!/^https?:\/\//i.test(trimmed)) {
    return trimTrailingSlashes(`https://${trimLeadingSlashes(trimmed)}`) || undefined;
  }

  return trimTrailingSlashes(trimmed);
};

const baseUrl = sanitizeBaseUrl(import.meta.env.VITE_PORTAL_BASE_URL as string | undefined);

const DEFAULT_SECTION_PATHS = {
  products: '/products',
  documents: '/documents',
  liveDemo: '/livedemo',
} as const;

const buildUrl = (value: string | undefined, fallback: string, base: string = baseUrl): string => {
  const target = (value ?? fallback).trim();
  if (!target) {
    return base;
  }

  if (/^https?:\/\//i.test(target)) {
    return target;
  }

  if (target === '/' || target === '') {
    return base;
  }

  const normalized = target.startsWith('/') ? target : `/${target}`;
  if (normalized === '/') {
    return base;
  }

  return `${base}${normalized}`;
};

const ensureLeadingSlash = (path: string): string => (path.startsWith('/') ? path : `/${path}`);

const resolveSectionPathValue = (value: string | undefined, fallback: string): string => {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : fallback;
};

const stripDomain = (url: string): string => url.replace(/^https?:\/\/[^/]+/i, '') || '/';

const normalizeParentPathForBase = (
  path: string,
  fallback: string,
  baseOverride: string | undefined,
): string => {
  if (!baseOverride) {
    return path;
  }

  const withoutDomain = stripDomain(path);
  const normalized = ensureLeadingSlash(withoutDomain.replace(/\/+/g, '/'));

  if (normalized === '/') {
    return '/';
  }

  if (normalized.startsWith(fallback)) {
    const remainder = normalized.slice(fallback.length);
    return remainder ? ensureLeadingSlash(remainder) : '/';
  }

  return normalized;
};

const normalizeParentForJoin = (parent: string): string => {
  const trimmed = parent.trim();
  if (!trimmed) {
    return '/';
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimTrailingSlashes(trimmed);
  }

  const withLeading = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  if (withLeading === '/') {
    return '/';
  }

  return trimTrailingSlashes(withLeading);
};

const joinPaths = (parent: string, child: string): string => {
  const normalizedChild = child.trim();
  if (!normalizedChild) {
    return normalizeParentForJoin(parent);
  }

  if (/^https?:\/\//i.test(normalizedChild)) {
    return normalizedChild;
  }

  const normalizedParent = normalizeParentForJoin(parent);
  const childWithLeading = ensureLeadingSlash(normalizedChild);

  if (normalizedParent === '/') {
    return childWithLeading;
  }

  return `${normalizedParent}${childWithLeading}`;
};

const rebaseAbsoluteChild = (
  absoluteChild: string,
  normalizedParent: string,
  fallbackParentNormalized: string,
): string => {
  const needsRebase =
    fallbackParentNormalized !== normalizedParent &&
    fallbackParentNormalized !== '/' &&
    (absoluteChild === fallbackParentNormalized ||
      absoluteChild.startsWith(`${fallbackParentNormalized}/`));

  if (!needsRebase) {
    if (normalizedParent === '/' || normalizedParent === fallbackParentNormalized) {
      return absoluteChild;
    }

    if (absoluteChild.startsWith(`${normalizedParent}/`) || absoluteChild === normalizedParent) {
      return absoluteChild;
    }

    const suffix = absoluteChild.replace(/^\/+/, '');
    return `${normalizedParent}/${suffix}`;
  }

  const remainder = absoluteChild.slice(fallbackParentNormalized.length);
  if (!remainder) {
    return normalizedParent === '/' ? '/' : normalizedParent;
  }

  const suffix = ensureLeadingSlash(remainder);
  if (normalizedParent === '/') {
    return suffix;
  }

  return `${normalizedParent}${suffix}`;
};

const resolveChildOverride = (
  value: string | undefined,
  parentPath: string,
  fallbackParentPath: string,
): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return undefined;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  const normalizedParent = normalizeParentForJoin(parentPath);
  const fallbackParentNormalized = normalizeParentForJoin(fallbackParentPath);

  if (trimmed.startsWith('/')) {
    const absoluteChild = ensureLeadingSlash(trimmed);
    return rebaseAbsoluteChild(absoluteChild, normalizedParent, fallbackParentNormalized);
  }

  const cleanChild = trimmed.replace(/^\/+/, '');
  if (normalizedParent === '/') {
    return ensureLeadingSlash(cleanChild);
  }

  if (
    fallbackParentNormalized !== '/' &&
    cleanChild.startsWith(fallbackParentNormalized.slice(1))
  ) {
    return `${normalizedParent}/${cleanChild.slice(fallbackParentNormalized.length - 1)}`;
  }

  return `${normalizedParent}/${cleanChild}`;
};

const buildSectionChildUrl = (
  value: string | undefined,
  parentPath: string,
  fallbackParentPath: string,
  defaultChild: string,
  base: string = baseUrl,
): string => {
  const override = resolveChildOverride(value, parentPath, fallbackParentPath);
  return buildUrl(override, joinPaths(parentPath, defaultChild), base);
};

const documentsBaseOverride = sanitizeOptionalBaseUrl(
  import.meta.env.VITE_PORTAL_BASE_URL_DOCUMENTS as string | undefined,
);
const liveDemoBaseOverride = sanitizeOptionalBaseUrl(
  import.meta.env.VITE_PORTAL_BASE_URL_LIVEDEMO as string | undefined,
);

const documentsBaseUrl = documentsBaseOverride ?? baseUrl;
const liveDemoBaseUrl = liveDemoBaseOverride ?? baseUrl;

const defaultDocumentsPath = documentsBaseOverride ? '/' : DEFAULT_SECTION_PATHS.documents;
const defaultLiveDemoPath = liveDemoBaseOverride ? '/' : DEFAULT_SECTION_PATHS.liveDemo;

const productsPathValue = resolveSectionPathValue(
  import.meta.env.VITE_PORTAL_PATH_PRODUCTS as string | undefined,
  DEFAULT_SECTION_PATHS.products,
);

const documentsPathValueRaw = resolveSectionPathValue(
  import.meta.env.VITE_PORTAL_PATH_DOCUMENTS as string | undefined,
  defaultDocumentsPath,
);

const liveDemoPathValueRaw = resolveSectionPathValue(
  import.meta.env.VITE_PORTAL_PATH_LIVEDEMO as string | undefined,
  defaultLiveDemoPath,
);

const documentsParentPath = normalizeParentPathForBase(
  documentsPathValueRaw,
  DEFAULT_SECTION_PATHS.documents,
  documentsBaseOverride,
);

const liveDemoParentPath = normalizeParentPathForBase(
  liveDemoPathValueRaw,
  DEFAULT_SECTION_PATHS.liveDemo,
  liveDemoBaseOverride,
);

const basePortalLinks = {
  base: baseUrl,
  home: buildUrl(import.meta.env.VITE_PORTAL_PATH_HOME as string | undefined, '/'),
  products: buildUrl(productsPathValue, DEFAULT_SECTION_PATHS.products),
  purchase: buildUrl(
    import.meta.env.VITE_PORTAL_PATH_PURCHASE as string | undefined,
    '/orders/create',
  ),
  contact: buildUrl(
    import.meta.env.VITE_PORTAL_PATH_CONTACT as string | undefined,
    '/contacts/create',
  ),
  cart: buildUrl(import.meta.env.VITE_PORTAL_PATH_CART as string | undefined, '/carts'),
  mypage: buildUrl(import.meta.env.VITE_PORTAL_PATH_MYPAGE as string | undefined, '/mypage'),
  mypageApi: buildUrl(
    import.meta.env.VITE_PORTAL_PATH_MYPAGE_API as string | undefined,
    '/mypage/services',
  ),
  service: buildUrl(import.meta.env.VITE_PORTAL_PATH_SERVICE as string | undefined, '/services'),
  serviceDetail: {
    dataloader: buildUrl(
      import.meta.env.VITE_PORTAL_PATH_SERVICE_DATALOADER as string | undefined,
      '/services/1',
    ),
    ocr: buildUrl(
      import.meta.env.VITE_PORTAL_PATH_SERVICE_OCR as string | undefined,
      '/services/2',
    ),
  },
  documents: buildUrl(documentsParentPath, documentsParentPath, documentsBaseUrl),
  liveDemo: buildUrl(liveDemoParentPath, liveDemoParentPath, liveDemoBaseUrl),
  productsDetail: {
    hwp: buildSectionChildUrl(
      import.meta.env.VITE_PORTAL_PATH_PRODUCTS_HWP as string | undefined,
      productsPathValue,
      DEFAULT_SECTION_PATHS.products,
      'sdks/1?type=HANGUEL',
    ),
    hcell: buildSectionChildUrl(
      import.meta.env.VITE_PORTAL_PATH_PRODUCTS_HCELL as string | undefined,
      productsPathValue,
      DEFAULT_SECTION_PATHS.products,
      'sdks/2?type=HANSEL',
    ),
    docs: buildSectionChildUrl(
      import.meta.env.VITE_PORTAL_PATH_PRODUCTS_DOCS as string | undefined,
      productsPathValue,
      DEFAULT_SECTION_PATHS.products,
      'sdks/3?type=DOCUMENT',
    ),
    ocr: buildSectionChildUrl(
      import.meta.env.VITE_PORTAL_PATH_PRODUCTS_OCR as string | undefined,
      productsPathValue,
      DEFAULT_SECTION_PATHS.products,
      'sdks/4?type=OCR',
    ),
    auth: buildSectionChildUrl(
      import.meta.env.VITE_PORTAL_PATH_PRODUCTS_AUTH as string | undefined,
      productsPathValue,
      DEFAULT_SECTION_PATHS.products,
      'sdks/5?type=AUTH',
    ),
  },
  // English specific product links for overseas
  productsDetailEn: {
    office: buildUrl(undefined, '/sdks/6'),
    spreadsheet: buildUrl(undefined, '/sdks/9'),
    docscompare: buildUrl(undefined, '/sdks/10'),
    ocr: buildUrl(undefined, '/sdks/8'),
    auth: buildUrl(undefined, '/sdks/7'),
  },
  // English specific document links for overseas
  documentsDetailEn: {
    spreadsheet: buildUrl(undefined, 'spreadsheet/intro', documentsBaseUrl),
    ocr: buildUrl(undefined, 'ocr/intro', documentsBaseUrl),
  },
  // Japanese specific document links for overseas
  documentsDetailJa: {
    spreadsheet: buildUrl(
      import.meta.env.VITE_PORTAL_PATH_DOCUMENTS_JA_SPREADSHEET as string | undefined,
      '/ja/spreadsheet',
      documentsBaseUrl,
    ),
    ocr: buildUrl(
      import.meta.env.VITE_PORTAL_PATH_DOCUMENTS_JA_OCR as string | undefined,
      '/ja/ocr',
      documentsBaseUrl,
    ),
  },
  documentsDetail: {
    hwp: buildSectionChildUrl(
      import.meta.env.VITE_PORTAL_PATH_DOCUMENTS_HWP as string | undefined,
      documentsParentPath,
      DEFAULT_SECTION_PATHS.documents,
      'hwp/intro',
      documentsBaseUrl,
    ),
    hcell: buildSectionChildUrl(
      import.meta.env.VITE_PORTAL_PATH_DOCUMENTS_HCELL as string | undefined,
      documentsParentPath,
      DEFAULT_SECTION_PATHS.documents,
      'hcell/overview',
      documentsBaseUrl,
    ),
    docs: buildSectionChildUrl(
      import.meta.env.VITE_PORTAL_PATH_DOCUMENTS_DOCS as string | undefined,
      documentsParentPath,
      DEFAULT_SECTION_PATHS.documents,
      'docscompare/topics/overview',
      documentsBaseUrl,
    ),
    ocr: buildSectionChildUrl(
      import.meta.env.VITE_PORTAL_PATH_DOCUMENTS_OCR as string | undefined,
      documentsParentPath,
      DEFAULT_SECTION_PATHS.documents,
      'ocr/topics/overview',
      documentsBaseUrl,
    ),
    dataloaderApi: buildSectionChildUrl(
      import.meta.env.VITE_PORTAL_PATH_DOCUMENTS_DATALOADER_API as string | undefined,
      documentsParentPath,
      DEFAULT_SECTION_PATHS.documents,
      'dataloader-api/intro',
      documentsBaseUrl,
    ),
    ocrApi: buildSectionChildUrl(
      import.meta.env.VITE_PORTAL_PATH_DOCUMENTS_OCR_API as string | undefined,
      documentsParentPath,
      DEFAULT_SECTION_PATHS.documents,
      'ocr-api/intro',
      documentsBaseUrl,
    ),
  },
  // English specific live demo links for overseas
  liveDemoDetailEn: {
    spreadsheet: buildUrl(undefined, 'spreadsheet-sdk', liveDemoBaseUrl),
    ocr: buildUrl(undefined, 'ocr-sdk', liveDemoBaseUrl),
  },
  liveDemoDetail: {
    hwp: buildSectionChildUrl(
      import.meta.env.VITE_PORTAL_PATH_LIVEDEMO_HWP as string | undefined,
      liveDemoParentPath,
      DEFAULT_SECTION_PATHS.liveDemo,
      'hwp-sdk',
      liveDemoBaseUrl,
    ),
    hcell: buildSectionChildUrl(
      import.meta.env.VITE_PORTAL_PATH_LIVEDEMO_HCELL as string | undefined,
      liveDemoParentPath,
      DEFAULT_SECTION_PATHS.liveDemo,
      'hcell-sdk',
      liveDemoBaseUrl,
    ),
    docs: buildSectionChildUrl(
      import.meta.env.VITE_PORTAL_PATH_LIVEDEMO_DOCS as string | undefined,
      liveDemoParentPath,
      DEFAULT_SECTION_PATHS.liveDemo,
      'docscompare-sdk',
      liveDemoBaseUrl,
    ),
    ocr: buildSectionChildUrl(
      import.meta.env.VITE_PORTAL_PATH_LIVEDEMO_OCR as string | undefined,
      liveDemoParentPath,
      DEFAULT_SECTION_PATHS.liveDemo,
      'ocr-sdk',
      liveDemoBaseUrl,
    ),
    dataloader: buildSectionChildUrl(
      import.meta.env.VITE_PORTAL_PATH_LIVEDEMO_DATALOADER as string | undefined,
      liveDemoParentPath,
      DEFAULT_SECTION_PATHS.liveDemo,
      'dataloader',
      liveDemoBaseUrl,
    ),
  },
} as const;

export type PortalLinks = typeof basePortalLinks;

const applyLanguagePrefixToUrl = (url: string, prefix: string): string => {
  if (!prefix) {
    return url;
  }

  try {
    const parsed = new URL(url);
    const path = parsed.pathname || '/';
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      return parsed.toString();
    }

    if (path === '/' || path === '') {
      parsed.pathname = prefix;
    } else {
      const trimmed = path.replace(/^\/+/, '');
      parsed.pathname = `${prefix}/${trimmed}`;
    }

    return parsed.toString();
  } catch {
    if (!url.startsWith('/')) {
      return url;
    }

    if (url === prefix || url.startsWith(`${prefix}/`)) {
      return url;
    }

    const trimmed = url.replace(/^\/+/, '');
    return `${prefix}/${trimmed}`;
  }
};

const cloneAndApplyLanguage = <T>(value: T, prefix: string, key?: string): T => {
  if (typeof value === 'string') {
    // Skip applying language prefix to base only (documents and liveDemo need /en/ prefix)
    if (key === 'base') {
      return value;
    }
    return applyLanguagePrefixToUrl(value, prefix) as T;
  }

  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [childKey, childValue] of Object.entries(value as Record<string, unknown>)) {
      // Skip applying language prefix to *En/*Ja objects (overseas-specific links)
      if (
        (key === 'productsDetailEn' ||
          key === 'documentsDetailEn' ||
          key === 'documentsDetailJa' ||
          key === 'liveDemoDetailEn') &&
        prefix
      ) {
        result[childKey] = childValue;
      } else {
        result[childKey] = cloneAndApplyLanguage(childValue, prefix, childKey);
      }
    }
    return result as T;
  }

  return value;
};

const DEFAULT_LANGUAGE = (import.meta.env.VITE_DEFAULT_LANGUAGE as string | undefined) ?? 'ko';

/**
 * Get portal links with language prefix
 *
 * Simple logic for 4 scenarios:
 * - 국내향 국문 (Domestic Korean): sdk.hancom.com
 * - 국내향 영문 (Domestic English): sdk.hancom.com/en
 * - 해외향 영문 (Overseas English): sdk.hancom.com
 * - 해외향 일문 (Overseas Japanese): sdk.hancom.com/ja
 *
 * @param language Current language ('ko' | 'en' | 'ja')
 * @param isOverseas Whether user is overseas (default: false)
 */
export const getPortalLinks = (language: string, isOverseas: boolean = false): PortalLinks => {
  // Add /en prefix for domestic English, /ja prefix for overseas Japanese
  const prefix =
    !isOverseas && language === 'en' ? '/en' : isOverseas && language === 'ja' ? '/ja' : '';
  const links = cloneAndApplyLanguage(basePortalLinks, prefix);

  if (!(isOverseas && language === 'ja')) return links;

  // For overseas JA: apply /ja/ prefix corrections
  // - mypage: base path is /orders (not /mypage)
  // - productsDetailEn, liveDemoDetailEn: skipped by cloneAndApplyLanguage, apply /ja/ manually
  return {
    ...links,
    mypage: applyLanguagePrefixToUrl(buildUrl(undefined, '/orders'), '/ja'),
    productsDetailEn: {
      office: applyLanguagePrefixToUrl(basePortalLinks.productsDetailEn.office, '/ja'),
      spreadsheet: applyLanguagePrefixToUrl(basePortalLinks.productsDetailEn.spreadsheet, '/ja'),
      docscompare: applyLanguagePrefixToUrl(basePortalLinks.productsDetailEn.docscompare, '/ja'),
      ocr: applyLanguagePrefixToUrl(basePortalLinks.productsDetailEn.ocr, '/ja'),
      auth: applyLanguagePrefixToUrl(basePortalLinks.productsDetailEn.auth, '/ja'),
    },
    liveDemoDetailEn: {
      spreadsheet: applyLanguagePrefixToUrl(basePortalLinks.liveDemoDetailEn.spreadsheet, '/ja'),
      ocr: applyLanguagePrefixToUrl(basePortalLinks.liveDemoDetailEn.ocr, '/ja'),
    },
  };
};

export const portalLinks = getPortalLinks(DEFAULT_LANGUAGE);
